import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '@resources/js/contexts/useIconDefaults';
import useTheme from '@resources/js/contexts/useTheme';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import React, { type JSX } from 'react';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import Table, { type TableColumn } from '../Table/Table';
import VerticalContent from '../VerticalContent/VerticalContent';

type BuildsCraftedGearSectionProps = {
  title: string;
  description: string;
  warning: string | null;
  table: {
    columns: Record<
      'item' | 'dps' | 'popularity',
      BuildsCraftedGearSectionViewModelColumn
    > &
      Partial<
        Record<'maxKey' | 'hps', BuildsCraftedGearSectionViewModelColumn>
      >;
    data: Array<{
      item: string;
      maxKey?: string;
      dps: string;
      hps?: string;
      popularity: string;
    }>;
  };
};

export type BuildsCraftedGearSectionViewModelColumn = Pick<
  TableColumn,
  'header' | 'textAlign' | 'noWrap'
>;

const iconDefaults = {
  item: { size: iconSizeLarge },
};

export default function BuildsCraftedGearSection({
  title,
  description,
  table,
}: BuildsCraftedGearSectionProps): JSX.Element | null {
  const theme = useTheme();

  if (table.data.length === 0) {
    return null;
  }

  return (
    <VerticalContent>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <IconDefaultsContextProvider overrides={iconDefaults}>
        {table.data.length > 0 ? (
          <Table
            {...{
              ...table,
              columns: {
                ...table.columns,
                item: {
                  ...table.columns.item,
                  maxWidth: 230,
                },
              },
            }}
            enhancedMarkdownEnabled={true}
            truncate={{
              limit: 6,
              showMoreButton: { styles: theme.primaryButtonStyles },
            }}
          />
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}
